import React, { useEffect, useState } from 'react'
import { SpeakerNotesOutlined, SpeakerNotesOffOutlined, HighlightOff } from '@mui/icons-material'
import {
  IconButton,
  Typography,
  Grid,
  TextField,
  Tooltip,
  Button,
  Autocomplete,
  Box,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import makeStyles from '@mui/styles/makeStyles'
import {
  getCommentId,
  getLegacySubfieldCommentId,
  getPageId,
  getReviewId,
  getTableCommentId,
  getTableFieldId,
  getTableReviewId,
  getTableRowId,
} from '../utils/utils'
import { FIELD_TYPES, STYLES } from '../../../utility/constants'
import moment from 'moment'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { fileServices } from '../../../services/fileServices'
import {
  useAllValues,
  useCommentValues,
  usePageDetails,
  useReviewValues,
  useTableStructure,
} from '../../../store'
import { useProcessContext } from '../../../store'
import { getTimeInMillis } from '../../../utility/datetime'
import { getTableRowFromLegacyAllValues } from '../utils/tableFieldUtil'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import { REVIEW_SCOPE_PAGE } from './DataEntryPageNav'

const useStyles = makeStyles(STYLES.COMMON)
const options = [
  { id: 0, label: 'this field' },
  { id: 1, label: 'all accepted fields in this page' },
  { id: 2, label: 'all rejected fields in this page' },
  { id: 3, label: 'all accepted fields in task' },
  { id: 4, label: 'all rejected fields in task' },
]
const ReviewSwitch = ({
  id,
  review,
  onReviewChange = () => {},
  comment,
  previousReviews = [],
  disabled = false,
  field,
  row,
}) => {
  const classes = useStyles()
  const { user } = useAuthContext()

  const [isDialogOpen, setDialogOpen] = useState(false)
  const processContext = useProcessContext((state) => state.processContext)
  const allFields = processContext.allFields

  const previousReviewsActivity = previousReviews.filter(
    (review) => review.id !== processContext.processData.id
  )
  const reviewValues = useReviewValues((state) => state.reviewValues)
  const allCommentValues = useCommentValues((state) => state.commentValues)
  const setCommentValues = useCommentValues((state) => state.setCommentValues)
  const getAllValues = useAllValues((state) => state.getAllValues)

  const pageDetails = usePageDetails((state) => state.pageDetails)
  const tableStructure = useTableStructure((state) => state.tableStructure)

  const [selectedOption, setSelectedOption] = useState(options[0])
  const [newComment, setNewComment] = useState(comment)
  const [isCommentInvalid, setIsCommentInvalid] = useState(false)

  useEffect(() => {
    setNewComment(comment)
  }, [comment])

  useEffect(() => {
    const trimmedComment = newComment?.replace(/[\s\n\r]+/g, '')
    const isEmpty = !trimmedComment || trimmedComment.length === 0

    if (review === false && isEmpty) {
      setIsCommentInvalid(true)
    } else {
      setIsCommentInvalid(false)
    }
  }, [newComment, review])

  const handleIconButtonClick = () => {
    const trimmedComment = newComment?.replace(/[\s\n\r]+/g, '')
    const isEmpty = !trimmedComment || trimmedComment.length === 0

    const nextReview = review === true ? false : true // toggle logic

    if (nextReview === false && isEmpty) {
      setIsCommentInvalid(true)
    } else {
      setIsCommentInvalid(false)
    }

    onReviewChange(nextReview)
  }

  const handleCommentChange = (
    comment,
    reviewValues,
    allCommentValues,
    accepted = false,
    scope
  ) => {
    let comments = {}

    // filtering fields based in scope
    let fields =
      scope === REVIEW_SCOPE_PAGE ? pageDetails[getPageId(field.page_template_id)] : allFields

    // filtering fields based on review scoped
    fields = fields.filter((it) =>
      processContext.activityTemplate?.is_scoped_review
        ? processContext?.activityTemplate?.scoped_review_field_template_ids?.includes(it.id) ||
          it.data_structure === FIELD_TYPES.TABLE
        : true
    )

    // looping through all fields
    fields.forEach((field) => {
      if (field.data_structure === FIELD_TYPES.TABLE) {
        let columns = []
        let rows = []

        const tableStructureValues = tableStructure[getTableFieldId(field.id)]
        if (tableStructureValues) {
          // getting table structure from store
          columns = tableStructureValues.columns
          rows = tableStructureValues.rows
        } else {
          // generating table structure on the fly
          columns = processContext?.tableSubfields?.find((it) => it.id === field.id)?.subfields
          const allValues = getAllValues()
          rows = getTableRowFromLegacyAllValues(allValues, columns)
        }

        columns = columns.filter((it) =>
          processContext.activityTemplate?.is_scoped_review
            ? processContext?.activityTemplate?.scoped_review_field_template_ids?.includes(it.id)
            : true
        )

        // generating comment values for table fields
        let tableCommentData = {}
        rows?.forEach((row) => {
          let rowCommentData = {}
          columns
            ?.filter((it) => !it.is_read_only)
            .forEach((col) => {
              // finally set the comment value based on condition
              if (reviewValues[getTableReviewId(col.id, row)] === accepted) {
                comments = {
                  ...comments,
                  [getTableCommentId(col.id, row)]: comment,
                }
                rowCommentData = {
                  ...rowCommentData,
                  [getCommentId(col.id)]: comment,
                }
              } else {
                rowCommentData = {
                  ...rowCommentData,
                  [getCommentId(col.id)]: allCommentValues[getTableCommentId(col.id, row)],
                }
              }
            })

          tableCommentData = {
            ...tableCommentData,
            [getTableRowId(row)]: {
              ...rowCommentData,
              updatedAt: getTimeInMillis(),
            },
          }
        })
        comments = {
          ...comments,
          [getTableFieldId(field.id)]: tableCommentData,
          updatedAt: getTimeInMillis(),
        }
      } else {
        if (!field.is_read_only && !field.is_subfield) {
          // applying comment to normal fields based on review status
          if (reviewValues[getReviewId(field.id)] === accepted) {
            comments = {
              ...comments,
              [getCommentId(field.id)]: comment,
              updatedAt: getTimeInMillis(),
            }
          }
        }
      }
    })

    return comments
  }
  const isCommentEmpty = (text) => {
    return !text || text.replace(/[\s\n\r]+/g, '').length === 0
  }

  const handleSave = () => {
    let updatedComments = {}

    if (selectedOption.id === 2) {
      updatedComments = handleCommentChange(
        newComment,
        reviewValues,
        allCommentValues,
        false,
        REVIEW_SCOPE_PAGE
      )
    } else if (selectedOption.id === 1) {
      updatedComments = handleCommentChange(
        newComment,
        reviewValues,
        allCommentValues,
        true,
        REVIEW_SCOPE_PAGE
      )
    } else if (selectedOption.id === 3) {
      updatedComments = handleCommentChange(newComment, reviewValues, allCommentValues, true)
    } else if (selectedOption.id === 4) {
      updatedComments = handleCommentChange(newComment, reviewValues, allCommentValues, false)
    } else {
      const cellKey = row ? getLegacySubfieldCommentId(id, row) : getCommentId(id)

      if (row) {
        const tableFieldTemplateId = processContext.tableSubfieldsMap?.find(
          (it) => it.subfield_template_id == id
        )?.table_field_template_id

        let tabularComments = allCommentValues[getTableFieldId(tableFieldTemplateId)]

        tabularComments = {
          ...tabularComments,
          [getTableRowId(row)]: {
            ...tabularComments[getTableRowId(row)],
            [getCommentId(id)]: newComment,
          },
        }

        updatedComments = {
          ...updatedComments,
          [cellKey]: newComment,
          [getTableFieldId(tableFieldTemplateId)]: tabularComments,
        }
      } else {
        updatedComments = {
          ...updatedComments,
          [cellKey]: newComment,
        }
      }
    }

    // finally update the store
    setCommentValues({ ...allCommentValues, ...updatedComments })
    setDialogOpen(false)
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'left',
        }}
      >
        <Tooltip
          placement="right"
          arrow
          title={
            previousReviewsActivity?.length ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                  maxHeight: previousReviewsActivity.length > 5 ? '370px' : 'auto',
                  overflowY: previousReviewsActivity.length > 5 ? 'auto' : 'visible',
                }}
              >
                {previousReviewsActivity?.map((revDatum, index) => (
                  <div key={index}>
                    <p style={{ margin: 0 }}>
                      {revDatum?.reviewerName?.length > 0 ? (
                        <>
                          <b>{revDatum?.reviewerName}</b> ({revDatum?.reviewerEmail})
                        </>
                      ) : (
                        <>Not reviewed yet!!</>
                      )}{' '}
                      - {revDatum?.reviewDatum ? <>&#9989;</> : <>&#10060;</>}
                    </p>
                    {revDatum?.associatedDataEntryActivityName ? (
                      <p style={{ margin: 0 }}>
                        Review of "{revDatum?.associatedDataEntryActivityName}" version{' '}
                        {revDatum?.associatedDataEntryVersion}
                      </p>
                    ) : null}
                    <p style={{ margin: 0 }}>
                      Reviewed Data: {revDatum?.reviewedDatum}{' '}
                      {revDatum?.fieldDataStructure == FIELD_TYPES.FILE &&
                      revDatum?.reviewedDatum?.length > 0 ? (
                        <Button
                          onClick={() => fileServices.downloadFile(user, revDatum?.reviewedDatum)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        >
                          &#11015;
                        </Button>
                      ) : null}
                    </p>
                    <p style={{ margin: 0 }}>
                      Review Comment: <>{revDatum?.reviewCommentDatum}</>
                    </p>
                    {revDatum?.timestamp?.length > 0 ? (
                      <p style={{ justifyContent: 'end', margin: 0 }}>
                        {moment(revDatum?.timestamp).format('DD MMM YYYY hh:mm a')}
                      </p>
                    ) : null}

                    {previousReviewsActivity.length !== index + 1 ? <hr /> : null}
                  </div>
                ))}
              </div>
            ) : null
          }
        >
          <div>
            <IconButton
            style={{maxWidth:18}}
              id={`review-icon-${review}`}
              size="small"
              aria-label="review-button"
              color={review ? 'success' : review === false ? 'error' : 'warning'}
              onClick={handleIconButtonClick}
              disabled={disabled}
            >
              {review === true ? (
                <CheckIcon fontSize="small" />
              ) : review === false ? (
                <CloseIcon fontSize="small" />
              ) : (
                <span style={{ color: 'warning' }}>?</span>
              )}
            </IconButton>
          </div>
        </Tooltip>

        <Tooltip
          title={
            review === true
              ? newComment?.trim().length > 0
                ? newComment
                : ''
              : newComment?.trim().length > 0
              ? newComment
              : 'Add Comments'
          }
        >
          <IconButton
            size="small"
            onClick={() => setDialogOpen(true)}
            color={isCommentInvalid ? undefined : 'default'}
            style={{
              alignSelf: 'center',
              color: isCommentInvalid ? '#d32f2f' : undefined,
            }}
          >
            {newComment?.trim().length > 0 ? (
              <SpeakerNotesOutlined fontSize="12px" />
            ) : (
              <SpeakerNotesOffOutlined fontSize="12px" />
            )}
          </IconButton>
        </Tooltip>
      </div>

      <DialogWrapper
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        heading={
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography className="fontWeight600 fontsize16" variant="h5">
              {disabled ? 'Review comments' : 'Enter Review Comments'}
            </Typography>
            <IconButton sx={{ padding: '5px' }} onClick={() => setDialogOpen(false)} size="large">
              <HighlightOff />
            </IconButton>
          </Grid>
        }
      >
        <TextField
          id={getCommentId(id)}
          className={classes.field}
          fullWidth
          minRows={5}
          multiline
          color="primary"
          variant="outlined"
          value={newComment}
          disabled={disabled}
          onChange={(e) => {
            const value = e.target.value
            setNewComment(value)
            if (!isCommentEmpty(value)) {
              setIsCommentInvalid(false)
            }
          }}
        />
        {!disabled ? (
          <>
            <Autocomplete
              options={options}
              getOptionLabel={(option) => option.label}
              value={selectedOption}
              onChange={(_, value) => setSelectedOption(value)}
              sx={{ marginTop: 2, marginBottom: 0 }}
              renderInput={(params) => (
                <TextField {...params} label="Select Option" variant="outlined" fullWidth />
              )}
            />
            <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button color="primary" variant="contained" onClick={handleSave}>
                Save
              </Button>
            </Box>
          </>
        ) : null}
      </DialogWrapper>
    </>
  )
}

export default ReviewSwitch
